export interface ContinueTitle {
  id: number;
  title: string;
  cover: string;
  type: 'MANGA' | 'ANIME';
  progress: number;
  total: number | null;
}
interface CollectionResponse {
  data?: { MediaListCollection?: { lists: { entries: { progress: number; media: { id: number; type: 'MANGA' | 'ANIME'; title: { userPreferred: string }; coverImage: { large: string }; chapters: number | null; episodes: number | null } }[] }[] } };
  errors?: { message: string }[];
}
const cache = new Map<string, { expires: number; promise: Promise<ContinueTitle[]> }>();

export function normalizeAniListUsername(value: string): string {
  const input = value.trim();
  if (/^https?:\/\//i.test(input)) {
    const url = new URL(input);
    if (!['anilist.co', 'www.anilist.co'].includes(url.hostname)) throw new Error('Pegá un perfil de anilist.co o escribí su nombre de usuario.');
    const match = url.pathname.match(/^\/user\/([^/]+)\/?$/i);
    if (!match) throw new Error('La URL debe ser la de tu perfil: anilist.co/user/tu-usuario.');
    return decodeURIComponent(match[1]);
  }
  return input.replace(/^@/, '');
}

export function getAniListContinue(username: string, options: { force?: boolean } = {}): Promise<ContinueTitle[]> {
  username = normalizeAniListUsername(username);
  if (!username || username.includes('@')) return Promise.reject(new Error('Ingresá tu usuario de AniList, no el correo ni el nombre de tu cuenta Yomiri.'));
  const key = username.toLowerCase();
  const existing = cache.get(key);
  if (!options.force && existing && existing.expires > Date.now()) return existing.promise;
  const promise = Promise.all((['ANIME', 'MANGA'] as const).map(async type => {
    const response = await fetch('https://graphql.anilist.co', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      signal: AbortSignal.timeout(15000),
      body: JSON.stringify({ query: `query($user:String!,$type:MediaType!){MediaListCollection(userName:$user,type:$type,status_in:[CURRENT,REPEATING],sort:UPDATED_TIME_DESC){lists{entries{progress media{id type title{userPreferred} coverImage{large} chapters episodes}}}}}`, variables: { user: username.trim(), type } }),
    });
    const result = await response.json().catch(() => ({})) as CollectionResponse;
    if (result.errors?.length) {
      const message = result.errors[0].message;
      throw new Error(/private/i.test(message) ? 'AniList indica que el perfil o su lista son privados. Revisá su privacidad en AniList.' : /not found/i.test(message) ? 'No encontramos ese usuario en AniList. Revisá el nombre del perfil.' : message);
    }
    if (!response.ok) throw new Error(response.status === 404 ? 'No encontramos ese perfil en AniList. Usá tu usuario de AniList o pegá la URL de tu perfil.' : response.status === 429 ? 'AniList está limitando las consultas. Intentá más tarde.' : `AniList no respondió (HTTP ${response.status}).`);
    if (!result.data?.MediaListCollection) throw new Error('No se pudo consultar esa lista pública.');
    const entries = result.data.MediaListCollection.lists.flatMap(list => list.entries);
    return [...new Map(entries.map(({ progress, media }) => [media.id, { id: media.id, title: media.title.userPreferred, cover: media.coverImage.large, type: media.type, progress, total: media.type === 'ANIME' ? media.episodes : media.chapters }])).values()].slice(0, 20);
  })).then(lists => lists.flat());
  cache.set(key, { promise, expires: Date.now() + 5 * 60000 });
  void promise.catch(() => { if (cache.get(key)?.promise === promise) cache.delete(key); });
  return promise;
}
